/**
 * The questions a skill asks before it starts ("What you need first"),
 * rendered as a small form. Text, single-choice and multi-choice inputs;
 * defaults pre-filled; required ones block the run until answered.
 */
import type { FC } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";

import type { SkillInput } from "@/features/firmKnowledge/types";

export type SkillInputValues = Record<string, string | string[]>;

/** The values a form starts with: each input's default, or empty. */
export const defaultInputValues = (inputs: SkillInput[]): SkillInputValues =>
  Object.fromEntries(
    inputs.map((spec) => {
      if (spec.kind === "multi") {
        return [spec.name, spec.default ? [spec.default] : []];
      }
      return [spec.name, spec.default ?? ""];
    })
  );

/** The required inputs still unanswered. */
export const missingInputs = (
  inputs: SkillInput[],
  values: SkillInputValues
): SkillInput[] =>
  inputs.filter((spec) => {
    if (!spec.required) return false;
    const value = values[spec.name];
    if (Array.isArray(value)) return value.length === 0;
    return !(value ?? "").trim();
  });

type Props = {
  inputs: SkillInput[];
  values: SkillInputValues;
  onChange: (values: SkillInputValues) => void;
  disabled?: boolean;
};

export const SkillInputsForm: FC<Props> = ({
  inputs,
  values,
  onChange,
  disabled
}) => {
  const set = (name: string, value: string | string[]) =>
    onChange({ ...values, [name]: value });

  return (
    <div className="space-y-4" data-testid="skill-inputs-form">
      {inputs.map((spec) => {
        const id = `skill-input-${spec.name}`;
        const value = values[spec.name];
        return (
          <div key={spec.name} className="space-y-1.5">
            <Label htmlFor={id} className="text-sm">
              {spec.prompt}
              {spec.required ? (
                <span className="text-danger" aria-hidden>
                  {" "}
                  *
                </span>
              ) : null}
            </Label>
            {spec.description ? (
              <p className="text-sm text-fg-3">{spec.description}</p>
            ) : null}
            {spec.kind === "text" ? (
              <Input
                id={id}
                value={typeof value === "string" ? value : ""}
                onChange={(e) => set(spec.name, e.target.value)}
                disabled={disabled}
                data-testid={id}
              />
            ) : null}
            {spec.kind === "choice" ? (
              <Select
                value={typeof value === "string" ? value : ""}
                onValueChange={(v) => set(spec.name, v)}
                disabled={disabled}
              >
                <SelectTrigger id={id} data-testid={id}>
                  <SelectValue placeholder="Choose one" />
                </SelectTrigger>
                <SelectContent>
                  {spec.choices.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            {spec.kind === "multi" ? (
              <div className="space-y-1.5" role="group" aria-labelledby={id}>
                {spec.choices.map((c) => {
                  const picked =
                    Array.isArray(value) && value.includes(c.value);
                  const boxId = `${id}-${c.value}`;
                  return (
                    <div key={c.value} className="flex items-center gap-2">
                      <Checkbox
                        id={boxId}
                        checked={picked}
                        disabled={disabled}
                        onCheckedChange={(checked) => {
                          const current = Array.isArray(value) ? value : [];
                          set(
                            spec.name,
                            checked
                              ? [...current, c.value]
                              : current.filter((v) => v !== c.value)
                          );
                        }}
                        data-testid={boxId}
                      />
                      <Label htmlFor={boxId} className="text-sm font-normal">
                        {c.label}
                      </Label>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};
